import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Warning, Spinner } from '@phosphor-icons/react';
import api from '../lib/api';

export default function TopupReturn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const code = params.get('code') || '';
  const paymentId = params.get('payment_id') || '';
  const [status, setStatus] = useState('polling'); // polling | confirmed | failed | timeout
  const [result, setResult] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!code) { setStatus('failed'); return; }
    if (!paymentId) {
      console.warn('topup_return_missing_payment_id', code);
      setStatus('no_payment_id');
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/organiser/trips/${code}/topup/${paymentId}/status`);
        if (cancelled) return;
        if (data.status === 'confirmed') {
          setResult(data);
          setStatus('confirmed');
          return true;
        }
        if (data.status === 'failed') {
          setStatus('failed');
          return true;
        }
      } catch {}
      return false;
    };

    const interval = setInterval(async () => {
      pollCount.current += 1;
      const done = await poll();
      if (done || pollCount.current >= 30) {
        clearInterval(interval);
        if (pollCount.current >= 30 && status === 'polling') setStatus('timeout');
      }
    }, 2000);

    // Immediate first poll
    poll();

    return () => { cancelled = true; clearInterval(interval); };
  }, [code, paymentId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center px-4" data-testid="topup-return">
      <Card className="w-full max-w-sm bg-white rounded-2xl border border-[#E1E6E4] p-6 text-center space-y-4">
        {status === 'polling' && (
          <>
            <Spinner className="w-10 h-10 text-[#1B4332] mx-auto animate-spin" />
            <h2 className="text-lg font-bold text-[#1D242B]">Payment received</h2>
            <p className="text-sm text-[#455561]">Updating your event...</p>
          </>
        )}
        {status === 'confirmed' && (
          <>
            <div className="w-14 h-14 bg-[#1B4332] rounded-full flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-white" weight="bold" />
            </div>
            <h2 className="text-lg font-bold text-[#1D242B]">Capacity Added</h2>
            {result && (
              <p className="text-sm text-[#455561]">
                Your event now has {result.max_players} players and {result.max_rounds} rounds.
              </p>
            )}
            <Button onClick={() => navigate(`/trip/${code}`)}
              className="w-full h-12 bg-[#1B4332] hover:bg-[#143726] text-white rounded-xl font-bold"
              data-testid="back-to-event-btn">
              Back to Event
            </Button>
          </>
        )}
        {status === 'failed' && (
          <>
            <div className="w-14 h-14 bg-[#D32F2F] rounded-full flex items-center justify-center mx-auto">
              <Warning className="w-7 h-7 text-white" weight="bold" />
            </div>
            <h2 className="text-lg font-bold text-[#1D242B]">Payment Failed</h2>
            <p className="text-sm text-[#455561]">Your payment was not completed. No changes have been made to your event.</p>
            <Button onClick={() => navigate(`/trip/${code}`)}
              className="w-full h-12 bg-[#1B4332] hover:bg-[#143726] text-white rounded-xl font-bold"
              data-testid="back-to-event-btn">
              Back to Event
            </Button>
          </>
        )}
        {status === 'timeout' && (
          <>
            <Spinner className="w-10 h-10 text-[#E59000] mx-auto" />
            <h2 className="text-lg font-bold text-[#1D242B]">Payment Processing</h2>
            <p className="text-sm text-[#455561]">
              If your allowance hasn't updated in a few minutes, contact support.
            </p>
            <Button onClick={() => navigate(`/trip/${code}`)}
              className="w-full h-12 bg-[#1B4332] hover:bg-[#143726] text-white rounded-xl font-bold"
              data-testid="back-to-event-btn">
              Back to Event
            </Button>
          </>
        )}
        {status === 'no_payment_id' && (
          <>
            <div className="w-14 h-14 bg-[#E59000] rounded-full flex items-center justify-center mx-auto">
              <Warning className="w-7 h-7 text-white" weight="bold" />
            </div>
            <h2 className="text-lg font-bold text-[#1D242B]">Payment Status Unknown</h2>
            <p className="text-sm text-[#455561]">
              We couldn't confirm your payment here. Go back to your event to check your updated allowance.
            </p>
            <Button onClick={() => navigate(`/trip/${code}`)}
              className="w-full h-12 bg-[#1B4332] hover:bg-[#143726] text-white rounded-xl font-bold"
              data-testid="back-to-event-btn">
              Back to Event
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
